/** @format */

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Card,
  Button,
  Table,
  TableColumn,
  Pagination,
  Icon,
  Input,
  Select,
  Option,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Dialog,
  Message
} from '@king-design/react'
import { TableSortValue } from '@king-design/react'
import MonitorCrumb from 'src/components/MonitorCrumb'
import UserInfo from 'src/components/UserInfo'
import {
  getPublicFunctionList,
  deleteFunctionBySign,
  getFunctionReach
} from 'src/api/ApiEventOpsFunction'
import { useAsyncFn, useAsync } from 'react-use'
import { formatDate } from 'src/utils/date'
import { useHistory, useLocation } from 'react-router-dom'
import DocView from './DocView'
import VersionHistory from './VersionHistory'
import { renderFunctionStatus } from './utils'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface SearchProps {
  Page: number
  PageSize: number
  FunctionName: string
  Type?: string // 类型：'custom' 表示自定义，'system' 表示系统预置
}

const initSearch: SearchProps = {
  Page: 1,
  PageSize: 10,
  FunctionName: '',
  Type: undefined
}

const Functions: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const initName = query.get('name') || ''
  const [search, setSearch] = useState<SearchProps>({
    ...initSearch,
    FunctionName: initName ? decodeURIComponent(initName) : ''
  })
  const [sort, setSort] = useState<TableSortValue>({})
  const [displayMode, setDisplayMode] = useState<'list' | 'doc'>('list')
  const [versionVisible, setVersionVisible] = useState(false)
  const [currentRecord, setCurrentRecord] = useState<any>(null)
  const [deleteVisible, setDeleteVisible] = useState(false)
  const [deleteRecord, setDeleteRecord] = useState<any>(null)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')

  // 获取公共函数列表
  const [{ value: functionList }, fetchFunctionList] = useAsyncFn(
    async (params?) => {
      const result = await getPublicFunctionList({
        ...params,
        Offset: displayMode === 'doc' ? 0 : (search.Page - 1) * search.PageSize,
        Limit: displayMode === 'doc' ? 0 : search.PageSize,
        Search: search.FunctionName,
        IsPublic: search.Type === 'system' ? 1 : search.Type === 'custom' ? -1 : undefined,
        Order: sort?.key && sort?.type ? `${sort?.key} ${sort?.type}` : undefined
      })
      return result
    },
    [search, sort, displayMode]
  )

  const handleVersionClick = (record: any) => {
    setCurrentRecord(record)
    setVersionVisible(true)
  }

  const handleDeleteClick = (record: any) => {
    setDeleteRecord(record)
    setDeleteConfirmInput('')
    setDeleteVisible(true)
  }

  const handleViewClick = (record: any) => {
    history.push(`/monitor/eventops/functions/detail/${record.Sign}`)
  }

  // 获取影响范围数据（用于删除确认）
  const { value: deleteReachData, loading: deleteReachLoading } = useAsync(async () => {
    if (deleteVisible && deleteRecord?.Sign) {
      const result = await getFunctionReach(deleteRecord.Sign)
      return result
    }
    return []
  }, [deleteVisible, deleteRecord?.Sign])

  const handleDelete = async () => {
    if (deleteRecord) {
      // 验证输入的函数名称是否匹配
      if (deleteConfirmInput !== deleteRecord.Sign) {
        Message.warning(_$('请输入正确的函数名称进行确认'))
        return
      }

      try {
        const result = await deleteFunctionBySign(deleteRecord.Sign)
        if (result?.code === 'Success') {
          Message.success(_$('删除成功'))
          setDeleteVisible(false)
          setDeleteRecord(null)
          setDeleteConfirmInput('')
          fetchFunctionList()
        } else {
          Message.error(result?.message || _$('删除失败'))
        }
      } catch (error) {
        Message.error(_$('删除失败'))
      }
    }
  }

  useEffect(() => {
    fetchFunctionList()
  }, [search, sort, displayMode])

  return (
    <div className={className}>
      <MonitorCrumb breadArr={[{ title: _$('自动化运维') }, { title: _$('公共函数') }]} />
      <Card style={{ marginBottom: 16 }}>
        <div className="search_row">
          <div className="search_inputs">
            <div className="search_item">
              <Input
                width={320}
                placeholder={_$('按函数名称搜索')}
                value={search.FunctionName}
                onChangeValue={value => {
                  setSearch({
                    ...search,
                    FunctionName: value,
                    Page: 1
                  })
                }}
                onKeyUp={e => {
                  if (e.keyCode === 13) {
                    fetchFunctionList()
                  }
                }}
              />
            </div>
            <div className="search_item">
              <Select
                width={320}
                placeholder={_$('类型')}
                value={search.Type || ''}
                onChangeValue={value => {
                  setSearch({
                    ...search,
                    Type: value === '' ? undefined : value,
                    Page: 1
                  })
                }}
              >
                <Option value="">{_$('全部')}</Option>
                <Option value="custom">{_$('自定义')}</Option>
                <Option value="system">{_$('系统预置')}</Option>
              </Select>
            </div>
          </div>
          <div className="ksc-space">
            <Dropdown trigger="click">
              <Button>
                {displayMode === 'list' ? _$('列表展示形式') : _$('文档展示形式')}
                <Icon className="ion-chevron-down" style={{ marginLeft: 4 }} />
              </Button>
              <DropdownMenu>
                <DropdownItem
                  onClick={() => {
                    setDisplayMode('list')
                  }}
                >
                  {_$('列表展示形式')}
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setDisplayMode('doc')
                  }}
                >
                  {_$('文档展示形式')}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Button
              type="primary"
              onClick={() => {
                history.push('/monitor/eventops/functions/create')
              }}
            >
              <Icon className="ion-plus" />
              新增函数
            </Button>
            <Button icon onClick={() => fetchFunctionList()}>
              <Icon className="ion-refresh" />
            </Button>
          </div>
        </div>
        {displayMode === 'list' ? (
          <>
            <Table
              data={functionList?.Info || []}
              sort={sort}
              onChangeSort={sort => {
                setSearch({
                  ...search,
                  Page: 1
                })
                setSort(sort)
              }}
              animation={false}
              rowKey={record => record.ID}
              checkType="none"
            >
              <TableColumn key="ID" title="ID" sortable width="80" />
              <TableColumn
                key="Sign"
                title="函数名称"
                sortable
                width="200"
                slotTemplate={([record]) => (
                  <a
                    onClick={() => {
                      history.push(`/monitor/eventops/functions/detail/${record.Sign}`)
                    }}
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                  >
                    {record.Sign}
                  </a>
                )}
              />
              <TableColumn key="Lang" title="语言类型" width="120" />
              <TableColumn
                key="Type"
                title="类型"
                width="120"
                slotTemplate={([record]) => {
                  if (record.IsPublic === 1) return '系统预置'
                  return '自定义'
                }}
              />
              <TableColumn
                key="Creator"
                title="操作人"
                sortable
                width="120"
                slotTemplate={([record]) =>
                  record.Creator ? <UserInfo name={record.Creator} /> : '-'
                }
              />
              <TableColumn
                key="PublishedAt"
                title="发布时间"
                sortable
                width="180"
                slotTemplate={([record]) => {
                  if (!record.PublishedAt) return '-'
                  return formatDate(record.PublishedAt, 'YYYY-MM-DD HH:mm:ss')
                }}
              />
              <TableColumn key="Version" title="版本" width="120" />
              <TableColumn
                key="Remark"
                title="备注"
                width="300"
                slotTemplate={([record]) => record.Remark || '-'}
              />
              <TableColumn
                key="Status"
                title="状态"
                width="120"
                slotTemplate={([record]) => renderFunctionStatus(record.Status, record.Approving)}
              />
              <TableColumn
                key="operations"
                title="操作"
                fixed="right"
                width="200"
                slotTemplate={([record]) => {
                  const isSystemBuiltIn = record.IsPublic === 1
                  return (
                    <div className="ksc-space">
                      <a onClick={() => handleViewClick(record)}>查看</a>
                      <a onClick={() => handleVersionClick(record)}>版本</a>
                      {/* 系统预置函数的删除按钮置灰但不隐藏 */}
                      <a
                        onClick={() => {
                          if (!isSystemBuiltIn) {
                            handleDeleteClick(record)
                          }
                        }}
                        style={{
                          color: isSystemBuiltIn ? '#999' : '#1890ff',
                          cursor: isSystemBuiltIn ? 'not-allowed' : 'pointer'
                        }}
                      >
                        删除
                      </a>
                    </div>
                  )
                }}
              />
            </Table>
            <Pagination
              value={search.Page}
              limit={search.PageSize}
              size="small"
              total={functionList?.Total || 0}
              onChangeValue={value => {
                setSearch({
                  ...search,
                  Page: value
                })
              }}
              onChangeLimit={limit => {
                setSearch({
                  ...search,
                  PageSize: limit
                })
              }}
            />
          </>
        ) : (
          <div className="doc-view-container">
            <DocView
              functionList={functionList?.Info || []}
              onVersionClick={handleVersionClick}
              onDeleteClick={handleDeleteClick}
              onViewClick={handleViewClick}
            />
          </div>
        )}
      </Card>

      {/* 版本历史弹窗 */}
      <VersionHistory
        visible={versionVisible}
        record={currentRecord}
        onClose={() => {
          setVersionVisible(false)
          setCurrentRecord(null)
        }}
      />

      {/* 删除确认弹窗 */}
      <Dialog
        value={deleteVisible}
        onClose={() => {
          setDeleteVisible(false)
          setDeleteRecord(null)
          setDeleteConfirmInput('')
        }}
        title={_$('确认删除')}
        width={600}
        slotFooter={
          <>
            <Button
              onClick={() => {
                setDeleteVisible(false)
                setDeleteRecord(null)
                setDeleteConfirmInput('')
              }}
              style={{ marginRight: 8 }}
            >
              {_$('取消')}
            </Button>
            <Button
              type="primary"
              onClick={handleDelete}
              disabled={
                deleteConfirmInput !== deleteRecord?.Sign ||
                (deleteReachData && deleteReachData.length > 0)
              }
            >
              {_$('确定')}
            </Button>
          </>
        }
      >
        <div style={{ padding: '16px 0' }}>
          {/* 影响范围部分 */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 500 }}>{_$('影响范围')}</h4>
            {deleteReachLoading ? (
              <p>{_$('加载中...')}</p>
            ) : deleteReachData && deleteReachData.length > 0 ? (
              <Table
                data={deleteReachData}
                rowKey={(record, idx) => `${record.ID || record.Sign}-${idx}`}
                animation={false}
                checkType="none"
                style={{ marginBottom: 16 }}
              >
                <TableColumn
                  key="Type"
                  title={_$('影响类型')}
                  width="150"
                  slotTemplate={([record]) => record.Type || '-'}
                />
                <TableColumn
                  key="Name"
                  title={_$('名称')}
                  width="250"
                  slotTemplate={([record]) => {
                    // 根据类型跳转到对应页面
                    if (record.Type === 'script_func') {
                      // 其他公共函数引用 -> 跳转到公共函数列表页
                      const name = record.Name ? encodeURIComponent(record.Name) : ''
                      const url = `/monitor/eventops/functions${name ? `?name=${name}` : ''}`
                      return (
                        <a
                          href={url}
                          onClick={e => {
                            e.preventDefault()
                            history.push(url)
                          }}
                          style={{ color: '#1890ff', cursor: 'pointer' }}
                        >
                          {record.Name || '-'}
                        </a>
                      )
                    } else if (record.Type === 'trigger') {
                      // 触发器引用 -> 跳转到触发器列表页
                      const name = record.Name ? encodeURIComponent(record.Name) : ''
                      const url = `/monitor/eventops/trigger${name ? `?name=${name}` : ''}`
                      return (
                        <a
                          href={url}
                          onClick={e => {
                            e.preventDefault()
                            history.push(url)
                          }}
                          style={{ color: '#1890ff', cursor: 'pointer' }}
                        >
                          {record.Name || '-'}
                        </a>
                      )
                    } else if (record.Type === 'scene') {
                      // 自动化场景引用 -> 跳转到自动化场景列表页
                      const name = record.Name ? encodeURIComponent(record.Name) : ''
                      const url = `/monitor/eventops/scene${name ? `?name=${name}` : ''}`
                      return (
                        <a
                          href={url}
                          onClick={e => {
                            e.preventDefault()
                            history.push(url)
                          }}
                          style={{ color: '#1890ff', cursor: 'pointer' }}
                        >
                          {record.Name || '-'}
                        </a>
                      )
                    }
                    return record.Name || '-'
                  }}
                />
                <TableColumn
                  key="Nodes"
                  title={_$('涉及节点')}
                  slotTemplate={([record]) => {
                    const nodes = record.Nodes
                    if (!nodes) return '-'
                    if (typeof nodes === 'string') return nodes
                    if (Array.isArray(nodes)) return nodes.join(', ')
                    return String(nodes)
                  }}
                />
              </Table>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 0',
                  color: '#999'
                }}
              >
                <Icon className="ion-ios-box-outline" style={{ fontSize: 48, marginBottom: 8 }} />
                <span>{_$('暂无数据')}</span>
              </div>
            )}
          </div>

          {/* 警告信息 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '4px',
              marginBottom: 24
            }}
          >
            <Icon
              className="ion-alert-circled"
              style={{ fontSize: 20, color: '#fa8c16', marginRight: 8 }}
            />
            <span style={{ color: '#fa8c16', fontSize: 14 }}>
              {deleteReachData && deleteReachData.length > 0
                ? '该公共函数已应用，不可删除，需要解除关联使用后，再删除'
                : _$('将删除此公共函数数据,且不可恢复!')}
            </span>
          </div>

          {/* 确认输入 */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ marginBottom: 8, fontSize: 14, color: '#262626' }}>
              {_$('请输入公共函数名称')} <strong>{deleteRecord?.Sign}</strong> {_$('进行删除确认')}
            </p>
            <Input
              value={deleteConfirmInput}
              onChangeValue={setDeleteConfirmInput}
              placeholder={deleteRecord?.Sign || ''}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default styled(Functions)`
  position: relative;
  padding-bottom: 64px;
  .search_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  .search_inputs {
    display: flex;
    align-items: center;
  }
  .k-pagination {
    margin-top: 16px;
    text-align: center;
  }
  .search_item {
    margin-right: 16px;
  }
  .status-tag {
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    padding: 2px 8px;
    .k-icon {
      margin-right: 4px;
    }
  }
  .status-enabled {
    color: #1890ff;
    border-color: #91d5ff;
    background-color: #e6f7ff;
    .k-icon {
      color: #1890ff;
    }
  }
  .status-approving {
    color: #13c2c2;
    border-color: #13c2c2;
    background-color: #e6fffb;
    .k-icon {
      color: #13c2c2;
    }
  }
  .status-draft {
    color: #262626;
    border-color: #d9d9d9;
    background-color: #fff;
    .k-icon {
      color: #262626;
    }
  }
  .status-disabled {
    color: #ff4d4f;
    border-color: #ffccc7;
    background-color: #fff;
    .k-icon {
      color: #ff4d4f;
    }
  }
  .status-icon-spin {
    animation: spin 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    display: inline-block;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  .doc-view-container {
    height: calc(100vh - 300px);
    overflow: hidden;
  }
`
